import {
    BarChart3,
    BookMarked,
    CircleX,
    GraduationCap,
    LayoutDashboard,
    Lightbulb,
    LogOut,
    Menu,
    Settings,
    Timer,
    X
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../state/auth";
import { Logo, cx } from "./ui";

const NAV_ITEMS = [
    { to: "/", label: "概览", icon: LayoutDashboard, end: true },
    { to: "/study", label: "闪卡学习", icon: GraduationCap, end: false },
    { to: "/topics", label: "知识点", icon: Lightbulb, end: false },
    { to: "/browse", label: "题库", icon: BookMarked, end: false },
    { to: "/mistakes", label: "错题集", icon: CircleX, end: false },
    { to: "/exam", label: "模拟考", icon: Timer, end: false },
    { to: "/stats", label: "统计", icon: BarChart3, end: false },
    { to: "/settings", label: "设置", icon: Settings, end: false }
] as const;

export function Layout() {
    const { displayName, signOut } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);
    const location = useLocation();

    return (
        <div className="min-h-screen bg-surface-sunken">
            <a
                href="#main"
                className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-brand focus:px-3 focus:py-2 focus:text-white"
            >
                跳到主要内容
            </a>

            <header className="sticky top-0 z-30 border-b border-line bg-surface/90 backdrop-blur">
                <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4">
                    <NavLink to="/" className="flex items-center gap-2 font-semibold text-ink">
                        <Logo className="size-7" />
                        <span className="hidden sm:inline">入籍考试训练</span>
                    </NavLink>

                    <nav aria-label="主导航" className="ml-4 hidden flex-1 items-center gap-1 md:flex">
                        {NAV_ITEMS.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    cx(
                                        "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-colors",
                                        isActive
                                            ? "bg-brand-soft font-medium text-brand"
                                            : "text-ink-soft hover:bg-surface-sunken hover:text-ink"
                                    )
                                }
                            >
                                <item.icon className="size-4" aria-hidden />
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="ml-auto flex items-center gap-2">
                        <span className="hidden text-sm text-ink-soft sm:inline">{displayName}</span>
                        <button
                            onClick={() => void signOut()}
                            className="hidden items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-ink-soft transition-colors hover:bg-surface-sunken hover:text-ink md:flex"
                        >
                            <LogOut className="size-4" aria-hidden />
                            退出
                        </button>
                        <button
                            onClick={() => setMenuOpen((open) => !open)}
                            aria-expanded={menuOpen}
                            aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
                            className="rounded-lg p-2 text-ink-soft transition-colors hover:bg-surface-sunken md:hidden"
                        >
                            {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
                        </button>
                    </div>
                </div>

                {menuOpen ? (
                    <nav aria-label="移动端导航" className="border-t border-line bg-surface px-4 py-2 md:hidden">
                        {NAV_ITEMS.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                onClick={() => setMenuOpen(false)}
                                className={({ isActive }) =>
                                    cx(
                                        "flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm",
                                        isActive ? "bg-brand-soft font-medium text-brand" : "text-ink-soft"
                                    )
                                }
                            >
                                <item.icon className="size-4" aria-hidden />
                                {item.label}
                            </NavLink>
                        ))}
                        <button
                            onClick={() => void signOut()}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-ink-soft"
                        >
                            <LogOut className="size-4" aria-hidden />
                            退出登录
                        </button>
                    </nav>
                ) : null}
            </header>

            <main id="main" key={location.pathname} className="mx-auto max-w-6xl px-4 py-6 sm:py-8">
                <Outlet />
            </main>
        </div>
    );
}
